// Canned replies for the live chat reply box: one tap fills the reply, Shift+tap sends immediately. Managers can add/remove.
import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, Zap } from 'lucide-react';
import { apiCall } from './_shared';

const MANAGE = ['super_admin', 'admin', 'manager'];

const CannedReplies = ({ userRole, onPick, onSend }) => {
  const [items, setItems] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [text, setText] = useState('');
  const [err, setErr] = useState('');
  const canManage = MANAGE.includes(userRole);

  const load = useCallback(() => {
    apiCall('/api/admin/chat-live/canned').then((d) => setItems(d.replies || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (label.trim().length < 2 || text.trim().length < 2) return;
    setErr('');
    try {
      await apiCall('/api/admin/chat-live/canned', { method: 'POST', body: JSON.stringify({ label: label.trim(), text: text.trim() }) });
      setLabel(''); setText(''); setAddOpen(false); load();
    } catch (e) { setErr(e.message); }
  };
  const remove = async (id) => {
    if (!window.confirm('Remove this quick reply?')) return;
    try { await apiCall(`/api/admin/chat-live/canned/${id}`, { method: 'DELETE' }); load(); } catch (e) { setErr(e.message); }
  };

  const chips = items.map((c) => (
    <span key={c.id} className="inline-flex items-center rounded-full border border-green-300 bg-green-50 text-green-900 text-xs overflow-hidden" data-testid={`canned-${c.id}`}>
      <button
        type="button"
        onClick={(e) => (e.shiftKey ? onSend(c.resolved) : onPick(c.resolved))}
        title={`${c.resolved}\n\nClick = insert · Shift+click = send now`}
        className="px-2.5 py-1 hover:bg-green-100 font-semibold"
        data-testid={`canned-pick-${c.id}`}
      >
        {c.label}
      </button>
      {canManage && (
        <button type="button" onClick={() => remove(c.id)} className="px-1.5 py-1 text-green-700 hover:bg-red-100 hover:text-red-700 border-l border-green-200" aria-label="Remove quick reply" data-testid={`canned-delete-${c.id}`}>
          <Trash2 className="w-3 h-3" />
        </button>
      )}
    </span>
  ));

  return (
    <div className="space-y-1" data-testid="canned-replies">
      <div className="flex flex-wrap items-center gap-1">
        <Zap className="w-3.5 h-3.5 text-green-700" />
        {chips}
        {canManage && (
          <button type="button" onClick={() => setAddOpen((v) => !v)} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 px-2 py-1" data-testid="canned-add-toggle">
            <Plus className="w-3 h-3" /> Quick reply
          </button>
        )}
      </div>
      {addOpen && (
        <div className="flex flex-col sm:flex-row gap-1" data-testid="canned-add-form">
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (e.g. Late check-out)" className="border rounded px-2 py-1 text-xs sm:w-44" data-testid="canned-add-label" />
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Reply text — placeholders: {checkin_from} {checkout_until} {breakfast} {phone} {email} {site}" className="border rounded px-2 py-1 text-xs flex-1" data-testid="canned-add-text" />
          <button type="button" onClick={add} disabled={label.trim().length < 2 || text.trim().length < 2} className="text-xs bg-slate-900 text-white rounded px-3 py-1 disabled:opacity-50" data-testid="canned-add-save">Add</button>
        </div>
      )}
      {err && <div className="text-xs text-red-600" data-testid="canned-error">{err}</div>}
    </div>
  );
};

export default CannedReplies;
